import { AlertCircle, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/common/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/common/ui/dialog";
import { Input } from "@/common/ui/input";
import { Label } from "@/common/ui/label";
import { Textarea } from "@/common/ui/textarea";
import { ApiError } from "@/lib/api-client";

import { createRole } from "../api/organization-api";
import { PermissionCheckboxGroup } from "./PermissionCheckboxGroup";
import { usePermissions } from "./use-permissions";

interface CreateRoleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  enterpriseId: string;
  onCreated: () => void;
}

export function CreateRoleModal({
  open,
  onOpenChange,
  enterpriseId,
  onCreated,
}: CreateRoleModalProps): React.ReactElement {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const {
    permissions,
    isLoading: permissionsLoading,
    error: permissionsError,
  } = usePermissions(open);

  useEffect(() => {
    if (!open) return;
    setName("");
    setDescription("");
    setSelectedPermissionIds([]);
    setError("");
    setIsSubmitting(false);
  }, [open]);

  const togglePermission = (permissionId: string): void => {
    setSelectedPermissionIds((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
    setError("");
  };

  const handleSubmit = async (): Promise<void> => {
    setError("");

    if (!name.trim()) {
      setError("Role name is required");
      return;
    }

    if (selectedPermissionIds.length === 0) {
      setError("Pick at least one permission for this role");
      return;
    }

    setIsSubmitting(true);
    try {
      const trimmedDescription = description.trim();
      await createRole(enterpriseId, {
        name: name.trim(),
        ...(trimmedDescription ? { description: trimmedDescription } : {}),
        permission_ids: selectedPermissionIds,
      });
      onCreated();
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          setError("A role with this name already exists in this enterprise.");
        } else if (err.status === 403) {
          setError("You don't have permission to create roles.");
        } else if (err.status === 400 || err.status === 422) {
          setError(err.message || "Please check the form and try again.");
        } else {
          setError("Something went wrong. Please try again.");
        }
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    if (isSubmitting) return;
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleCancel();
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Role</DialogTitle>
          <DialogDescription>
            Define a custom role for this enterprise. Pick the permissions members assigned this
            role will have.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="role-name">Name</Label>
            <Input
              id="role-name"
              data-testid="create-role-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError("");
              }}
              placeholder="e.g. Fitness Coach"
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="role-description">Description (optional)</Label>
            <Textarea
              id="role-description"
              data-testid="create-role-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short summary of what this role can do"
              rows={2}
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label>Permissions</Label>
            {permissionsLoading ? (
              <div
                data-testid="create-role-permissions-loading"
                className="flex items-center gap-2 text-sm text-muted-foreground"
              >
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading permissions…
              </div>
            ) : permissionsError ? (
              <div
                data-testid="create-role-permissions-error"
                className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{permissionsError}</span>
              </div>
            ) : (
              <PermissionCheckboxGroup
                permissions={permissions}
                selectedIds={selectedPermissionIds}
                onToggle={togglePermission}
                disabled={isSubmitting}
              />
            )}
          </div>
        </div>

        {error && (
          <div
            data-testid="create-role-error"
            className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            data-testid="create-role-submit"
            onClick={handleSubmit}
            disabled={isSubmitting || permissionsLoading}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating…
              </>
            ) : (
              "Create Role"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
